import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useIntercom } from "react-use-intercom";

import CustomStepper from "../../Components/Others/custom-stepper";
import TextField from "../../Components/Form Fields/text-field";
import EmailField from "../../Components/Form Fields/email-field";
import PhoneNumber from "../../Components/Form Fields/phone-number";
import DatePickerField from "../../Components/Form Fields/date-picker-field";
import OTPField from "../../Components/Form Fields/otp-field";
import PrimaryButton from "../../Components/Buttons/primary-button";
import CheckBox from "../../Components/Form Fields/checkbox";

import { useDirectSignupCustomer, useLoginWith2FACode, useRequestLogin2FACode } from "../../CustomHooks/Mutations";

import { useAppUtilityContext } from "../../Contexts/AppUtilityContext";
import { useViewport } from "../../Contexts/ViewportContext";

import withFormProvider from "../../HOC/withFormProvider";

import { validation } from "../../Utils/validations";
import {
  dispatchAPIErrorToast,
  dispatchErrorToast,
  dispatchWarningToast,
  LocalStorage,
  onSignOut,
  transformUSNumberToCountryCodeFormat,
  getStepCount,
} from "../../Utils/helpers";
import { appUtilityKeys, debugProps } from "../../Utils/constants";

import { StakeLogo } from "../../Assets/Images";
import { CashBack, EyeSlashFilled, Gauge, Lock, Map } from "../../Assets/Icons";

const Benefits = [
  {
    Icon: Gauge,
    title: "Credit Builder",
    description: "Build your credit automatically with every on-time rent payment.",
  },
  {
    Icon: CashBack,
    title: "Unlimited Cash Back",
    description: "Earn cash back on every purchase with your Stake Debit.",
  },
  {
    Icon: EyeSlashFilled,
    title: "Zero Hidden Fees",
    description: "Stake Checking comes with zero monthly fees, overdraft fees, or surprises.",
  },
  {
    Icon: Map,
    title: "Neighborhood Network (Coming soon)",
    description: "Get instant cash back when you shop, dine, or spend at local partner businesses near you.",
  },
];

const getValidationsForName = (nameLabel) => {
  return {
    isAllowedCharacters: (name) =>
      validation.name.isAllowedCharacters(name) || `Your ${nameLabel} must not contain unaccepted characters`,

    hasAtmost50Characters: (name) =>
      validation.name.hasAtmost50Characters(name) || `Your ${nameLabel} can't exceed 50 characters`,
  };
};

function SignUp() {
  const [showSignUpSteps, setShowSignUpSteps] = useState(true);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [timeLeftToResendOTP, setTimeLeftToResendOTP] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const resendOtpRef = useRef(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { width } = useViewport();

  const { show: showIntercom, update: updateIntercom } = useIntercom();

  const { onUserCredentialsUpdate } = useAppUtilityContext();

  const { register, watch, getValues, getFieldState, formState, handleSubmit } = useFormContext();

  const { isLoading: isRequestingLogin2FACode, mutate: RequestLogin2FACode } = useRequestLogin2FACode({
    onSuccess: () => {
      if (resendOtpRef.current) {
        dispatchWarningToast(`New OTP code was sent at ${phoneNumber}`, { key: "request2FA" });
      } else {
        setShowOTPVerification(true);
      }
      setTimeLeftToResendOTP(30);
      setCanResend(false);
    },
    onError: () => dispatchErrorToast("Oops! Something went wrong!", { key: "request2FA" }),
    onSettled: () => (resendOtpRef.current = false),
  });

  const SendOTPToPhone = () =>
    RequestLogin2FACode({
      ...debugProps,
      phoneNumber: transformUSNumberToCountryCodeFormat(phoneNumber),
    });

  const { isLoading: isLoadingDirectSignupCustomer, mutate: DirectSignupCustomer } = useDirectSignupCustomer({
    onSuccess: SendOTPToPhone,
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error, key: "directSignupCustomer" }),
  });

  const { isLoading: isLoggingInWith2FACode, mutate: LoginWith2FACode } = useLoginWith2FACode({
    onSuccess: (data) => {
      onSignOut({ redirectOnLogout: false });
      queryClient.resetQueries();
      onUserCredentialsUpdate(data);
      navigate("/login");
    },
    onError: (err) => dispatchAPIErrorToast({ message: err?.response?.data?.error?.msg, key: "loginWith2FA" }),
  });

  useEffect(() => {
    const agentId = new URLSearchParams(window.location.search).get("agentId");
    if (!!agentId) {
      LocalStorage.write(appUtilityKeys.agentId, agentId);
    }
  }, []);

  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: false });
    return () => updateIntercom({ hideDefaultLauncher: true });
  }, [updateIntercom]);

  useEffect(() => {
    let timer;
    if (timeLeftToResendOTP > 0) {
      timer = setInterval(() => {
        setTimeLeftToResendOTP((prevTime) => prevTime - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }

    return () => clearInterval(timer);
  }, [timeLeftToResendOTP]);

  const handleResendClick = () => {
    if (canResend) {
      resendOtpRef.current = true;
      SendOTPToPhone();
    }
  };

  const onSubmit = () => {
    if (!showOTPVerification) {
      DirectSignupCustomer({
        user: {
          first_name: firstName?.trim(),
          last_name: lastName?.trim(),
          email: email?.trim(),
          birthdate: dob,
          phone: phoneNumber,
        },
        agent_id: LocalStorage.read(appUtilityKeys.agentId),
      });
    } else {
      LoginWith2FACode({
        ...debugProps,
        phoneNumber: transformUSNumberToCountryCodeFormat(phoneNumber),
        phoneNumberCode: getValues("otp"),
      });
    }
  };

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const dob = watch("dob");
  const email = watch("email");
  const phoneNumber = watch("phoneNumber");
  const checkboxValue = watch("checkboxValue") ?? false;

  const firstNameProps = register("firstName", {
    required: "Please enter your first name",
    validate: {
      ...getValidationsForName("first name"),
      hasAtleast2Characters: (name) =>
        validation.name.hasAtleast2Characters(name) || "Your first name must be at least 2 characters long",
    },
  });

  const lastNameProps = register("lastName", {
    required: "Please enter your last name",
    validate: getValidationsForName("last name"),
  });

  const checkboxProps = register("checkboxValue", {
    required: "Please check this box to continue",
  });

  const { error: firstNameFieldError } = getFieldState(firstNameProps.name, formState);
  const { error: lastNameFieldError } = getFieldState(lastNameProps.name, formState);
  const { error: checkboxFieldError } = getFieldState(checkboxProps.name, formState);

  const isLoading = isLoadingDirectSignupCustomer || isRequestingLogin2FACode || isLoggingInWith2FACode;

  return (
    <div
      className={`onboarding-page signup justify-content-center ${
        showSignUpSteps ? "signup-steps" : !showOTPVerification ? "fill-details" : "verify-phone"
      }`}
    >
      <div className="max-width-container">
        {showSignUpSteps ? (
          <div className="signup-card">
            <div className="signup-steps-content">
              <h2 className="signup-steps-title">Create your Stake Account</h2>
              <p className="signup-steps-description">
                Get set up to earn unlimited Cash Back and build credit in 2 minutes.
              </p>
            </div>
            <CustomStepper
              className="with-content"
              steps={[
                {
                  label: "Tell us who you are",
                  StepComponent: () => getStepCount(1),
                  subComponent: <div className="description">Add your personal details to get started.</div>,
                },
                {
                  label: "Tell us where you live",
                  StepComponent: () => getStepCount(2),
                  subComponent: <div className="description">Add your address and lease details.</div>,
                },
                {
                  label: "Upload your signed lease",
                  StepComponent: () => getStepCount(3),
                  subComponent: <div className="description">Add a signed copy so we can verify your residence.</div>,
                },
                {
                  label: "Finish your Stake Saver enrollment and checkout",
                  StepComponent: () => getStepCount(4),
                  subComponent: (
                    <div className="description">
                      A Stake Checking account will be created for you. You will then fund your account in order to pay
                      rent, earn Cash Back, and build credit.
                    </div>
                  ),
                },
              ]}
              orientation="vertical"
              alternativeLabel={false}
            />
            <PrimaryButton onClick={setShowSignUpSteps.bind(this, false)}>Get Started</PrimaryButton>
          </div>
        ) : (
          <>
            <StakeLogo className="stake-logo" />
            <div className="signup-card">
              <div className="signup-benefit-section">
                <StakeLogo className="stake-logo" />
                <div className="signup-benefit-header">
                  <h2 className="signup-benefit-title">
                    Join Stake. Rent smarter. {width >= 1200 ? <br></br> : <></>}
                    <span className="color--primary-400">Earn more.</span>
                  </h2>
                  <p className="signup-benefit-description">
                    Join thousands of renters who are building credit and earning Cash Back with every payment.
                  </p>
                </div>
                <div className="signup-benefits">
                  {Benefits.map((benefit, idx) => (
                    <div key={idx} className="signup-benefit">
                      <div className="icon-wrapper">
                        <benefit.Icon />
                      </div>
                      <div className="content">
                        <h5 className="benefit-title">{benefit.title}</h5>
                        <p className="benefit-description">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <form className="signup-form" onSubmit={handleSubmit(onSubmit)}>
                <StakeLogo className="stake-logo" />
                <p className="form-name">Sign up to Stake</p>
                <h2 className="form-title mb-0">
                  {!showOTPVerification ? "Let’s set up your account" : "Verify your phone number"}
                </h2>
                {!showOTPVerification ? (
                  <Row className="row-gap-4 mt-6 mb-8">
                    <Col xl={6} lg={12} md={6} xs={12}>
                      <TextField
                        className="filled"
                        label="First Name"
                        placeholder="Official registered first name"
                        {...firstNameProps}
                        error={!!firstNameFieldError}
                        errorMessage={firstNameFieldError?.message}
                      />
                    </Col>
                    <Col xl={6} lg={12} md={6} xs={12}>
                      <TextField
                        className="filled"
                        label="Last Name"
                        placeholder="Official registered last name"
                        {...lastNameProps}
                        error={!!lastNameFieldError}
                        errorMessage={lastNameFieldError?.message}
                      />
                    </Col>
                    <Col xs={12}>
                      <DatePickerField
                        name="dob"
                        label="Date of Birth"
                        validations={{
                          isAValidDate: validation.date.isAValidDate,
                          isAbove18YearsOfAge: validation.date.isAbove18YearsOfAge,
                        }}
                        textFieldProps={{ placeholder: "MM / DD / YYYY", fullWidth: true }}
                        datePickerProps={{ disableFuture: true, disableOpenPicker: true }}
                        required="Please enter your date of birth"
                        variant="filled"
                        format="MM / DD / YYYY"
                      />
                    </Col>
                    <Col xs={12}>
                      <EmailField
                        className="filled"
                        label="Email Address"
                        placeholder="email@example.com"
                        autoFocus={false}
                      />
                    </Col>
                    <Col xs={12}>
                      <PhoneNumber
                        className="filled"
                        label="Phone number"
                        placeholder="(000) 000-0000"
                        autoFocus={false}
                      />
                    </Col>
                    <Col xs={12}>
                      <div className="d-flex align-items-start gap-2">
                        <CheckBox {...checkboxProps} checked={checkboxValue} />
                        <p className="signup-terms mb-0">
                          By creating an account, I agree to the Stake's{" "}
                          <a href="https://www.stake.rent/legal/end-user-terms" target="_blank" rel="noreferrer">
                            End User Terms
                          </a>{" "}
                          and acknowledge the{" "}
                          <a href="https://www.stakerent.com/legal/privacy-policy" target="_blank" rel="noreferrer">
                            Privacy Policy
                          </a>
                          . By submitting, you authorize Stake Network, Inc to send text messages with offers and other
                          information. Message/data rates apply. Consent is not a condition of purchase.
                        </p>
                      </div>
                      {checkboxFieldError && (
                        <p className="text-xs-regular color--error-red mt-1 mb-0">{checkboxFieldError.message}</p>
                      )}
                    </Col>
                  </Row>
                ) : (
                  <>
                    <p className="form-description">
                      Enter the 6-digit code we sent to <span className="highlight">{phoneNumber}</span>
                    </p>
                    <div className="otp-field my-lg-10 my-8">
                      <OTPField />
                    </div>
                  </>
                )}
                <PrimaryButton type="submit" disabled={isLoading}>
                  {!showOTPVerification ? "Create account" : "Verify"} {isLoading && <CircularProgress />}
                </PrimaryButton>
                {!showOTPVerification ? (
                  <div className="form-action-text text-center mt-4">
                    Already have an account?{" "}
                    <Link className="form-action-button" to="/login">
                      Sign in
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="form-action-text mt-5">
                      Didn’t receive the code?{" "}
                      <button
                        className="form-action-button"
                        type="button"
                        onClick={handleResendClick}
                        disabled={!canResend}
                      >
                        Resend code{!canResend && ` in ${timeLeftToResendOTP}s`}
                      </button>
                    </div>
                    <div className="form-action-text mt-2">
                      Wrong phone number?{" "}
                      <button className="form-action-button" type="button" onClick={showIntercom.bind(this)}>
                        Update
                      </button>
                    </div>
                  </>
                )}
                <div className="spacer"></div>
                <div className="data-secure-label mt-4">
                  <Lock />
                  Your personal data is encrypted and stored securely
                </div>
              </form>
            </div>
            <div className="data-secure-label mt-4">
              <Lock />
              Your personal data is encrypted and stored securely
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default withFormProvider(SignUp);
